import React, { useState } from "react";
import {
  Box,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  Collapse,
  List,
  ListItem,
  ListItemText,
  IconButton,
  TableFooter
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { IReferredUserActivity } from "../../models";
import theme from "@/styles/theme";
import { parseMoney } from "@/utils/parseMoney";
import { formatDateTime } from "@/components/datagrid-base/utils/datagrid";
import isMobile from "is-mobile";
import { ExpandableDgContainer } from "./styles";

interface ExpandableDatagridProps {
  payload: IReferredUserActivity[];
}

const headerCellSX = {
  color: "black",
  fontFamily: "sans-serif",
  fontWeight: 800 as const
};

const containerSX = {
  border: "1px solid",
  borderColor: "divider",
  width: "100%",
  textAlignLast: "center" as const
};

export const ExpandableDatagrid: React.FC<ExpandableDatagridProps> = ({
  payload
}) => {
  const events = payload?.[0]?.events ?? [];

  const parseEvent = (event: string) => {
    switch (event) {
      case "PURCHASE":
        return "Compra";
      case "WITHDRAWAL":
        return "Saque";
      case "MGM":
        return "Bônus";
      default:
        return event;
    }
  };
  const parseStatus = (status: string) => {
    switch (status) {
      case "CONFIRMED":
        return "Confirmado";
      default:
        return status;
    }
  };

  if (!isMobile()) {
    if (!events.length) {
      return (
        <Box>
          <Typography variant="body2" color="text.secondary" fontWeight="bold">
            Sem eventos
          </Typography>
        </Box>
      );
    }

    return (
      <ExpandableDgContainer>
        <Box sx={containerSX}>
          <Table>
            <TableHead>
              <TableRow
                sx={{ backgroundColor: theme.colors.palete.brownExtraLighter }}
              >
                {["Evento", "Status", "Comissão", "Data"].map((label) => (
                  <TableCell key={label} sx={headerCellSX}>
                    {label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {events.map(
                (
                  { eventType, eventStatus, eventDate, originalAmount },
                  index
                ) => (
                  <TableRow
                    key={index}
                    sx={{
                      "& > *": { borderBottom: "unset" },
                      backgroundColor:
                        index % 2
                          ? theme.colors.palete.brownExtraLighter
                          : theme.colors.palete.tableOdd
                    }}
                  >
                    <TableCell>{parseEvent(eventType)}</TableCell>
                    <TableCell>{parseStatus(eventStatus)}</TableCell>
                    <TableCell>{parseMoney(originalAmount ?? 0)}</TableCell>
                    <TableCell>{formatDateTime(eventDate)}</TableCell>
                  </TableRow>
                )
              )}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TableCell sx={headerCellSX}>Valor das comissões</TableCell>
                <TableCell></TableCell>
                <TableCell sx={headerCellSX}>
                  {events
                    .reduce(
                      (acc, event) => acc + (event.originalAmount ?? 0),
                      0
                    )
                    .toLocaleString("pt-BR", {
                      style: "currency",
                      currency: "BRL"
                    })}
                </TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableFooter>
          </Table>
        </Box>
      </ExpandableDgContainer>
    );
  } else {
    const [open, setOpen] = useState(false);

    return (
      <Box
        sx={{
          width: "100%",
          border: "1px solid",
          borderColor: "divider",
          p: 1
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between"
          }}
        >
          <Typography variant="h6" sx={{ fontFamily: "sans-serif" }}>
            Eventos
          </Typography>
          <IconButton onClick={() => setOpen(!open)} size="small">
            {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
        <Collapse in={open} timeout="auto" unmountOnExit>
          {events.length ? (
            <List>
              {events.map((event, index) => (
                <ListItem key={index} divider>
                  <ListItemText
                    primary={`${parseEvent(event.eventType)} - ${parseStatus(
                      event.eventStatus
                    )}`}
                    secondary={`${parseMoney(
                      event.commissionAmount
                    )} | ${formatDateTime(event.eventDate)}`}
                  />
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Sem eventos
            </Typography>
          )}
        </Collapse>
      </Box>
    );
  }
};
